/**
 * ZeroMonth — Prest0 as a month. Face 2 of the signature object.
 *
 * Face 1 (Zero.tsx) is a day you step through hour by hour. This is the same
 * mark one scale up: the full circle is ONE Prest0n's monthly allocation, and
 * the five arcs are what a real month of work spends it on. The arithmetic —
 * and the reason the circle genuinely IS the month rather than being drawn to
 * look like it — is in zeroMonth.ts.
 *
 * IT IS A DISPLAY YOU CAN READ, NOT A SECOND CALCULATOR. Select an arc and it
 * tells you what that slice is, how many, at what published rate, and what
 * share of the month it takes. That is deliberately the SAME contract as face
 * 1: select and read. The reasons matter —
 *
 *   - It is one object with several faces, so the faces should behave alike.
 *     A ring you step through and a ring you drag numbers into are two
 *     different controls wearing one shape.
 *   - The modal calculator (WorkUnitCalculator) already does editing, with
 *     five sliders and the same maths, and it is good. Rebuilding it inline
 *     with a picture on top would be a duplicate, and duplicates drift.
 *
 * So this answers "what does a month look like?" at a glance and the launcher
 * beneath it still answers "what would MY month cost?". Whether the modal is
 * later redundant is a decision to make with this on the page, not before.
 *
 * `monthArcs()` already accepts arbitrary counts, so making the ring editable
 * later is a change to this file only.
 */

import React from 'react';
import styled from 'styled-components';
import { media } from '../../styles/breakpoints';
// The ring's behaviour, words and geometry are ONE hook (./useMonthRing.ts, 2026-09-30), read here and by the pricing
// page's rebuild in the new design, so the two rings cannot behave or read differently. The five arcs step through the
// gold's own opacity (ARC_TONES) rather than inventing colours; an invented green cost this work a design-token gate
// failure once already.
import { useMonthRing, arcPath, ARC_TONES, RING } from './useMonthRing';

const { R, WIDTH: RING_W, CX, CY } = RING;

const Frame = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 1.75rem;
  align-items: center;
  justify-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 1.1rem;
  }
`;

const Dial = styled.svg`
  /* Below the tablet breakpoint the BARS below are the control instead.
     display:none (not visibility) so the hidden one leaves the accessibility
     tree and the tab order — there is never a second slider for one month.
     No backticks in this comment: it is inside a template literal. */
  display: none;
  ${media.tablet} {
    display: block;
  }
  width: 260px;
  height: 260px;
  border-radius: 50%;

  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 6px;
  }
`;

  /* A DIV, not a <ul> (2026-09-12, WCAG AA burn-down). The slider ARIA is
     spread onto this element, and role="slider" REPLACES a <ul>'s implicit
     list role — which orphaned every child as a listitem with no list, twelve
     of the audit's rows across two components. The accessible object here is
     one slider with a value, not a list of seven things, so the markup now
     says that. Nothing visual changes: the list resets below were already
     zeroing everything a <ul> brings.
     Keeping the resets anyway is deliberate — they are inert on a div and
     they cost nothing, where removing them invites the element being changed
     back to a <ul> without them. */
const Bars = styled.div`
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 30rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;

  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 4px;
  }

  ${media.tablet} {
    display: none;
  }
`;

const Bar = styled.div<{ $on: boolean }>`
  cursor: pointer;
  padding: 0.35rem 0;
`;

const BarHead = styled.div<{ $on: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.84rem;
  line-height: 1.45;
  color: ${(p) => (p.$on ? 'var(--brand-text)' : 'var(--brand-text-secondary)')};
  font-weight: ${(p) => (p.$on ? 600 : 400)};
`;

const BarTrack = styled.div`
  margin-top: 0.3rem;
  height: 8px;
  border-radius: 999px;
  background: var(--brand-surface);
  border: 1px solid var(--brand-line);
  overflow: hidden;
`;

const BarFill = styled.div<{ $share: number; $tone: number }>`
  height: 100%;
  width: ${(p) => Math.min(100, p.$share * 100)}%;
  background: var(--brand-gold);
  opacity: ${(p) => p.$tone};
`;

const Readout = styled.div`
  max-width: 26rem;
`;

const Slice = styled.div`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.15rem;
  color: var(--brand-text);
  line-height: 1.3;
`;

const Detail = styled.div`
  margin-top: 0.4rem;
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--brand-text-secondary);
`;

const Whole = styled.div`
  margin-top: 0.9rem;
  padding-top: 0.9rem;
  border-top: 1px solid var(--brand-line);
  font-size: 0.84rem;
  line-height: 1.6;
  color: var(--brand-text-secondary);
`;

const Hint = styled.div`
  margin-top: 0.6rem;
  font-size: 0.78rem;
  color: var(--brand-text-placeholder);
`;

const Live = styled.span`
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0);
  white-space: nowrap; border: 0;
`;

export interface ZeroMonthProps {
  /** Auto-advance through the slices until the visitor takes over. */
  autoPlay?: boolean;
}

export const ZeroMonth: React.FC<ZeroMonthProps> = ({ autoPlay = true }) => {
  /**
   * ONE SLIDER CONTRACT, spread onto whichever presentation CSS is showing —
   * the same arrangement Zero.tsx and DayStrip use, for the same reason: two
   * copies drift, and the copy that drifts is the phone.
   */
  const { arcs, index, take, sliderProps, words } = useMonthRing({ autoPlay });

  return (
    <Frame>
      <Dial viewBox="0 0 260 260" {...sliderProps} data-month-ring="">
        {/* the unspent ring beneath — the month, before anything is on it */}
        <circle
          cx={CX} cy={CY} r={R}
          fill="none"
          stroke="var(--brand-line)"
          strokeWidth={RING_W}
          opacity="0.5"
        />
        {arcs.map((a, i) => (
          <path
            key={a.key}
            d={arcPath(a.startAngle, a.endAngle)}
            fill="none"
            stroke="var(--brand-gold)"
            strokeWidth={i === index ? RING_W + 6 : RING_W}
            opacity={i === index ? 1 : ARC_TONES[i % ARC_TONES.length]}
            style={{ cursor: 'pointer' }}
            onClick={() => take(i)}
          />
        ))}

        {/* the total, in the middle of its own circle */}
        <text
          x={CX} y={CY - 4}
          textAnchor="middle"
          fontFamily="'Space Grotesk', sans-serif"
          fontSize="26"
          fill="var(--brand-text)"
        >
          {words.total}
        </text>
        <text
          x={CX} y={CY + 18}
          textAnchor="middle"
          fontFamily="'Inter', sans-serif"
          fontSize="12"
          fill="var(--brand-text-secondary)"
        >
          {words.totalUnit}
        </text>
      </Dial>

      {/* The same month as stacked bars, for phones. Only one of the two is
          ever present to a user — the swap is display:none on each. */}
      <Bars {...sliderProps} data-month-bars="">
        {arcs.map((a, i) => (
          <Bar key={a.key} $on={i === index} onClick={() => take(i)} data-month-slice={i === index ? 'current' : ''}>
            <BarHead $on={i === index}>
              <span>{a.item}</span>
              <span>{words.barFigure(a)}</span>
            </BarHead>
            <BarTrack>
              <BarFill $share={a.share} $tone={i === index ? 1 : ARC_TONES[i % ARC_TONES.length]} />
            </BarTrack>
          </Bar>
        ))}
      </Bars>

      <Readout>
        <Slice>{words.slice}</Slice>
        <Detail>
          {words.detailLead}<strong>{words.detailFigure}</strong>{words.detailTail}
        </Detail>
        {/* NAMING (Miles, 2026-09-10): the mark is Prest0, never "the 0".
            Tightened to one claim — the arithmetic IS the argument, and the
            old sentence spent its last clause explaining its own joke. */}
        <Whole>{words.whole}</Whole>
        <Hint>{words.hint}</Hint>
        <Live aria-live="polite">{words.live}</Live>
      </Readout>
    </Frame>
  );
};

export default ZeroMonth;
